import { DOCUMENT, isPlatformServer } from "@angular/common";
import { DestroyRef, Injectable, PLATFORM_ID, inject } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { CookieService } from "ngx-cookie-service";
import {
  EMPTY,
  Observable,
  Subscription,
  distinctUntilChanged,
  filter,
  fromEvent,
  interval,
  merge,
  startWith,
  switchMap,
  take,
} from "rxjs";
import { OmniarkModalService } from "../../shared/services/modal.service";
import { showSessionExpiredModal } from "../interceptors/auth.interceptor";
import { SESSION_EXPIRES_COOKIE } from "../../shared/utils/token-encoding.utils";
import { AuthService } from "./auth.service";

export { SESSION_EXPIRES_COOKIE };

/** How often a signed-in tab looks for the cookie. */
export const SESSION_CHECK_INTERVAL_MS = 30_000;

/**
 * Opens the "Session Expired" dialog in a tab nobody is using, once the
 * session has ended — without a request and without sliding the session.
 *
 * The interceptor only finds out when a request gets a 401 and the refresh
 * fails, and a hidden tab sends none (the feature-flag poll stops while the
 * tab is hidden), so a tab left in the background would otherwise sit on
 * stale data until it is looked at again. Instead, while
 * `signedIn$` holds, this checks for the `session_expires_at` cookie
 * immediately, every 30 s (hidden tabs included — the check is local and
 * free) and whenever the tab becomes visible.
 *
 * **Presence, not a timestamp.** The server sets the cookie with the refresh
 * token's Max-Age and the browser deletes it on its own clock, so client/server
 * clock skew does not matter. Cookies are shared by every tab: a refresh in
 * another tab slides it for all of them, and a logout in another tab clears
 * it, which ends the session here too. The cookie's value is informational.
 *
 * **Seen first.** A session issued before the server set this cookie has none
 * until its next refresh. The dialog fires only when a cookie this session
 * has seen goes away — never because it was never there.
 *
 * On OK the user is logged out back to this page, unless the cookie is back
 * (another tab signed in again meanwhile): then this tab reloads into that
 * session instead (`AuthService.resumeSession`). The dialog itself is the
 * interceptor's, behind its once-only guard, so this and a failing request
 * can never open two.
 *
 * Server-side revocation (an admin ending the session, Redis eviction) leaves
 * the cookie in place and is still only discovered by the next foreground
 * request.
 */
@Injectable({
  providedIn: "root",
})
export class SessionExpiryService {
  private readonly platformId = inject(PLATFORM_ID);
  private readonly destroyRef = inject(DestroyRef);
  private readonly document = inject(DOCUMENT);
  private readonly cookies = inject(CookieService);
  private readonly modal = inject(OmniarkModalService);
  private readonly auth = inject(AuthService);

  private watchSubscription: Subscription | null = null;
  /** True once this session's cookie has been seen; reset on sign-in/out. */
  private cookieSeen = false;

  /**
   * Follow `signedIn$` (AuthService's) for the life of the app. Called once
   * from `provideAppInitializer`; repeat calls and the server platform are
   * no-ops.
   */
  start(signedIn$: Observable<boolean>): void {
    if (this.watchSubscription || isPlatformServer(this.platformId)) return;

    const becameVisible$ = fromEvent(this.document, "visibilitychange").pipe(
      filter(() => this.document.visibilityState !== "hidden"),
    );

    this.watchSubscription = signedIn$
      .pipe(
        distinctUntilChanged(),
        switchMap((signedIn) => {
          this.cookieSeen = false;
          if (!signedIn) return EMPTY;
          return merge(
            interval(SESSION_CHECK_INTERVAL_MS),
            becameVisible$,
          ).pipe(
            startWith(null),
            filter(() => this.sessionEnded()),
            // Ended: no more checks this session.
            take(1),
          );
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() =>
        showSessionExpiredModal(this.modal, (returnUrl) => {
          if (this.cookies.check(SESSION_EXPIRES_COOKIE)) {
            this.auth.resumeSession();
          } else {
            this.auth.logout(returnUrl);
          }
        }),
      );
  }

  /** Records the cookie when present; true once a seen cookie is gone. */
  private sessionEnded(): boolean {
    if (this.cookies.check(SESSION_EXPIRES_COOKIE)) {
      this.cookieSeen = true;
      return false;
    }
    return this.cookieSeen;
  }
}
